import React, { useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Divider,
  Chip,
} from "@mui/material";
import { Verified as VerifiedIcon } from "@mui/icons-material";
import { RootState, AppDispatch } from "../../../redux/store";
import { fetchOrganization } from "../../../redux/actions/organizationActions";
import { fetchLatestDeployment } from "../../../redux/actions/deploymentActions";
import { fetchAllBuilds } from "../../../redux/actions/buildActions";
import { fetchOrganizationAssessmentLinks } from "../../../redux/actions/assessmentActions";
import { fetchAllRoleplays } from "../../../redux/actions/roleplayActions";
import UsersTab from "../UsersTab";
import AgentConfigsTab from "../AgentConfigsTab";
import AssessmentsTab from "../AssessmentsTab";
import ResLinksTab from "../ResLinksTab";
import MemorySchemaConfig from "./MemorySchemaConfig";

const OrganizationView: React.FC = () => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();

  const { currentOrganization, loading, error } = useSelector(
    (state: RootState) => state.organizations
  );

  const { latestDeployment, loading: deploymentLoading } = useSelector(
    (state: RootState) => state.deployments
  );

  const { builds } = useSelector((state: RootState) => state.builds);

  const { user: adminUser } = useSelector((state: RootState) => state.base);

  useEffect(() => {
    if (organizationId) {
      dispatch(fetchOrganization(organizationId));
      dispatch(fetchLatestDeployment(organizationId));
      dispatch(fetchOrganizationAssessmentLinks(organizationId));
      dispatch(fetchAllRoleplays({ organizationId }));
      dispatch(fetchAllBuilds());
    }
  }, [dispatch, organizationId]);

  useEffect(() => {
    // If a toast message was passed via navigation state, show it.
    if (location.state && (location.state as any).toastMessage) {
      toast.success((location.state as any).toastMessage);
      // Clear the state so the toast isn't shown again.
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate]);

  if (loading) {
    return <CircularProgress />;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (!currentOrganization) {
    return <div>Organization not found</div>;
  }

  return (
    <Box sx={{ p: 4 }}>
      <ToastContainer position="top-center" autoClose={3000} />
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Typography variant="h5">Organization Details</Typography>
        {currentOrganization.is_prod_release && (
          <Chip
            icon={<VerifiedIcon />}
            label="Production Release"
            color="primary"
            size="small"
          />
        )}
        {currentOrganization.requires_redaction && (
          <Chip label="Requires Redaction" color="warning" size="small" />
        )}
      </Box>

      <TextField
        label="Organization Name"
        value={currentOrganization.name}
        fullWidth
        margin="normal"
        InputProps={{
          readOnly: true,
          sx: {
            backgroundColor: "#f5f5f5",
            opacity: 0.7,
          },
        }}
        InputLabelProps={{ shrink: true }}
      />

      <TextField
        label="Organization ID"
        value={currentOrganization.id}
        fullWidth
        margin="normal"
        InputProps={{
          readOnly: true,
          sx: {
            backgroundColor: "#f5f5f5",
            opacity: 0.7,
          },
        }}
        InputLabelProps={{ shrink: true }}
      />

      <TextField
        label="Created At"
        value={new Date(currentOrganization.created_at).toLocaleString()}
        fullWidth
        margin="normal"
        InputProps={{
          readOnly: true,
          sx: {
            backgroundColor: "#f5f5f5",
            opacity: 0.7,
          },
        }}
        InputLabelProps={{ shrink: true }}
      />

      <TextField
        label="Updated At"
        value={new Date(currentOrganization.updated_at).toLocaleString()}
        fullWidth
        margin="normal"
        InputProps={{
          readOnly: true,
          sx: {
            backgroundColor: "#f5f5f5",
            opacity: 0.7,
          },
        }}
        InputLabelProps={{ shrink: true }}
      />

      {/* Memory Schema Configuration */}
      {organizationId &&
        adminUser?.scopes?.includes("memoryconfigview:view") && (
          <>
            <MemorySchemaConfig
              organizationId={organizationId}
              config={currentOrganization.memory_schema_config}
            />
          </>
        )}

      <Box display="flex" alignItems="center" marginTop={"16px"}>
        <Button
          variant="contained"
          color="warning"
          onClick={() => navigate("/dashboard/organizations")}
        >
          Back
        </Button>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Deployments section */}
      {organizationId && (
        <>
          <Typography variant="h5" gutterBottom>
            Deployments
          </Typography>
          <Box sx={{ mb: 4 }}>
            {deploymentLoading ? (
              <CircularProgress size={24} />
            ) : latestDeployment ? (
              <Box>
                <TextField
                  label="Latest Deployment ID"
                  value={latestDeployment.id}
                  fullWidth
                  margin="normal"
                  InputProps={{
                    readOnly: true,
                    sx: {
                      backgroundColor: "#f5f5f5",
                      opacity: 0.7,
                    },
                  }}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Build"
                  value={
                    builds?.find((b) => b.id === latestDeployment.build_id)
                      ?.build_name
                      ? `${builds.find((b) => b.id === latestDeployment.build_id)?.build_name} (${latestDeployment.build_id})`
                      : latestDeployment.build_id
                  }
                  fullWidth
                  margin="normal"
                  InputProps={{
                    readOnly: true,
                    sx: {
                      backgroundColor: "#f5f5f5",
                      opacity: 0.7,
                    },
                  }}
                  InputLabelProps={{ shrink: true }}
                />
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2 }}
                >
                  <TextField
                    label="Behavior Config ID"
                    value={latestDeployment.behavior_config_id}
                    fullWidth
                    InputProps={{
                      readOnly: true,
                      sx: {
                        backgroundColor: "#f5f5f5",
                        opacity: 0.7,
                      },
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                  <Button
                    variant="text"
                    color="primary"
                    onClick={() =>
                      navigate(
                        `/dashboard/behavior-configs/${latestDeployment.behavior_config_id}`
                      )
                    }
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    View Config
                  </Button>
                </Box>
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() =>
                      navigate(
                        `/dashboard/deployments/${latestDeployment.id}?organization_id=${organizationId}`
                      )
                    }
                  >
                    Edit Latest Deployment
                  </Button>

                  {/* Uncomment this button if you want to allow creating a new deployment from the organization view which might be better if we start passing more params*/}
                  {/* <Button
                    variant="contained"
                    color="success"
                    onClick={() => navigate(`/dashboard/deployments/create?organization_id=${organizationId}`)}
                    style={{ marginLeft: "8px" }}
                  >
                    Create New Deployment
                  </Button> */}
                </Box>
              </Box>
            ) : (
              <Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  No deployments found for this organization.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() =>
                    navigate(
                      `/dashboard/deployments/create?organization_id=${organizationId}`
                    )
                  }
                >
                  Create First Deployment
                </Button>
              </Box>
            )}
          </Box>
        </>
      )}

      <Divider sx={{ my: 4 }} />

      {/* Users belonging to this organization */}
      {organizationId && (
        <>
          <Typography variant="h5" gutterBottom>
            Organization Users
          </Typography>
          <UsersTab organizationId={organizationId} />
        </>
      )}

      <Divider sx={{ my: 4 }} />

      {/* Agent Configs belonging to this organization */}
      {organizationId && (
        <>
          <Typography variant="h5" gutterBottom>
            Agent Configurations
          </Typography>
          <AgentConfigsTab organizationId={organizationId} />
        </>
      )}

      <Divider sx={{ my: 4 }} />

      {/* Assessments belonging to this organization */}
      {organizationId && (
        <>
          <AssessmentsTab organizationId={organizationId} />
        </>
      )}

      <Divider sx={{ my: 4 }} />

      {/* Resource Links for this organization */}
      {organizationId && (
        <>
          <Typography variant="h5" gutterBottom>
            Resource Links (Assessments & Roleplays)
          </Typography>
          <ResLinksTab organizationId={organizationId} />
        </>
      )}
    </Box>
  );
};

export default OrganizationView;
